import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import Button from '../common/Button';
import { TextInput, SelectInput } from '../common/form';

const initialState = {
    name: '',
    branchId: '',
};

const LevelForm = ({ role = 'ADMIN', initial, onSubmit, onCancel, submitLabel }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState(initial || initialState);
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [branchesErr, setBranchesErr] = useState(null);

    useEffect(() => {
        if (initial) setForm((p) => ({ ...p, ...initial, branchId: initial.branchId ?? p.branchId }));
        // eslint-disable-next-line
    }, [initial?.levelId]);

    useEffect(() => {
        const ac = new AbortController();
        const load = async () => {
            setLoadingBranches(true);
            setBranchesErr(null);
            try {
                const base =
                    role === 'ADMIN' ? '/admin/branches' :
                        role === 'MANAGER' ? '/manager/branches/mine' :
                            '/coach/branches';
                const res = await API.get(base, { signal: ac.signal, params: { active: true, page: 0, size: 200 } });
                const content = Array.isArray(res.data?.content) ? res.data.content : (Array.isArray(res.data) ? res.data : []);
                setBranches(content);
            } catch (e) {
                if (!ac.signal.aborted) setBranchesErr(t('pages.levels.form.loadBranchesFailed'));
            } finally {
                setLoadingBranches(false);
            }
        };
        load();
        return () => ac.abort();
    }, [role, t]);

    const isEdit = Boolean(initial?.levelId ?? initial?.id);
    const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            name: form.name.trim(),
            branchId: Number(form.branchId),
        };
        onSubmit(payload);
    };

    return (
        <form onSubmit={handleSubmit} className="form-shell space-y-4">
            <div className="form-section">
                <TextInput
                    label={t('pages.levels.form.levelName')}
                    name="name"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    required
                />

                <SelectInput
                    label={t('pages.levels.form.branchForLevel')}
                    name="branchId"
                    required
                    disabled={isEdit}
                    value={form.branchId}
                    onChange={(e) => setField('branchId', e.target.value)}
                    note={isEdit ? t('pages.levels.form.branchFixedNote') : undefined}
                    error={branchesErr || undefined}
                    className={isEdit ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''}
                >
                    <option value="">{t('forms.common.select')}</option>
                    {branches.map((b) => (
                        <option key={b.id ?? b.branchId} value={b.id ?? b.branchId}>
                            {b.name} (#{b.id ?? b.branchId})
                        </option>
                    ))}
                </SelectInput>
                {loadingBranches ? <p className="field-note">{t('pages.levels.form.loadingBranches')}</p> : null}
            </div>

            <div className="flex gap-2 justify-end">
                <Button type="button" variant="secondary" onClick={onCancel}>{t('forms.common.cancel')}</Button>
                <Button type="submit" variant="primary">{submitLabel ?? t('forms.common.save')}</Button>
            </div>
        </form>
    );
};

export default LevelForm;
